import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Label,
  Input,
  Badge,
  Table,
  Alert,
  Spinner,
} from "reactstrap";
import axios from "axios";
import { useDomainFlow } from "./DomainResellerFlow";
import { SearchResult } from "./types";

const baseUrl = process.env.REACT_APP_BASE_URL;

interface DomainPricingData {
  purchasePrice?: number;
  renewalPrice?: number;
  transferPrice?: number;
  privacyPrice?: number;
  whoisPrivacyPrice?: number;
}

const DomainPricing: React.FC = () => {
  const { state, updateState, nextStep, previousStep } = useDomainFlow();
  const [years, setYears] = useState<number>(1);
  const [whoisPrivacy, setWhoisPrivacy] = useState<boolean>(false);
  const [whoisPrivacyPrice, setWhoisPrivacyPrice] = useState<number>(0);
  const [pricingData, setPricingData] = useState<DomainPricingData | null>(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState<boolean>(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [supportsPrivacy, setSupportsPrivacy] = useState<boolean>(false);
  const [isLoadingPrivacyInfo, setIsLoadingPrivacyInfo] = useState<boolean>(false);

  const selectedDomain: SearchResult | null = state.selectedDomain;

  // Fetch domain info to check if privacy is supported
  useEffect(() => {
    if (!selectedDomain?.domainName) return;

    const fetchDomainInfo = async () => {
      setIsLoadingPrivacyInfo(true);
      try {
        const response = await axios.get(
          `${baseUrl}/companyFormation/getDomainInfo`,
          {
            params: {
              domainName: selectedDomain.domainName,
            },
            withCredentials: true,
          }
        );

        if (response && response.status === "success" && response.domainInfo) {
          setSupportsPrivacy(response.domainInfo.supportsPrivacy === true);
        }
      } catch (error: any) {
        console.error("Error fetching domain info:", error);
        // Default to true if we can't determine (show option, let API handle validation)
        setSupportsPrivacy(true);
      } finally {
        setIsLoadingPrivacyInfo(false);
      }
    };

    fetchDomainInfo();
  }, [selectedDomain?.domainName]);

  // Fetch pricing from API when domain or years change
  useEffect(() => {
    if (!selectedDomain?.domainName) return;

    const fetchPricing = async () => {
      setIsLoadingPricing(true);
      setPricingError(null);

      try {
        const response = await axios.get(
          `${baseUrl}/companyFormation/getDomainPricing`,
          {
            params: {
              domainName: selectedDomain.domainName,
              years: years,
            },
            withCredentials: true,
          }
        );

        if (response && response.status === "success" && response.pricing) {
          const pricing = response.pricing;
          setPricingData({
            purchasePrice: pricing.purchasePrice,
            renewalPrice: pricing.renewalPrice,
            transferPrice: pricing.transferPrice,
          });
        } else {
          setPricingError(response.error || "Failed to fetch pricing");
          // Fallback to search result pricing
          setPricingData({
            purchasePrice: selectedDomain.purchasePrice,
            renewalPrice: selectedDomain.renewalPrice,
          });
        }
      } catch (error: any) {
        console.error("Error fetching domain pricing:", error);
        let errorMessage = "Failed to fetch pricing information.";
        
        if (error.response) {
          if (error.response.status === 400) {
            errorMessage = error.response.data?.error || "Invalid request.";
          } else if (error.response.status === 404) {
            errorMessage = "Pricing not available for this domain.";
          } else if (error.response.status === 429) {
            errorMessage = "Rate limit exceeded. Please try again later.";
          } else {
            errorMessage = error.response.data?.error || "Failed to fetch pricing.";
          }
        } else if (error.request) {
          errorMessage = "Network error. Please check your connection.";
        }

        setPricingError(errorMessage);
        // Fallback to search result pricing
        setPricingData({
          purchasePrice: selectedDomain.purchasePrice,
          renewalPrice: selectedDomain.renewalPrice,
        });
      } finally {
        setIsLoadingPricing(false);
      }
    };

    fetchPricing();
  }, [selectedDomain?.domainName, years]);

  // Fetch WHOIS privacy pricing when years change or privacy is enabled
  useEffect(() => {
    if (!selectedDomain?.domainName || !supportsPrivacy) return;

    const fetchPrivacyPricing = async () => {
      try {
        const response = await axios.post(
          `${baseUrl}/companyFormation/getWhoisPrivacyPricing`,
          {
            domainName: selectedDomain.domainName,
            years: years,
          },
          { withCredentials: true }
        );

        if (response && response.status === "success" && response.privacyPricing) {
          const privacyPricing = response.privacyPricing;
          if (privacyPricing.purchasePrice !== undefined && privacyPricing.purchasePrice !== null) {
            setWhoisPrivacyPrice(privacyPricing.purchasePrice);
          }
        }
      } catch (error: any) {
        console.error("Error fetching WHOIS privacy pricing:", error);
        // Fallback: calculate as 15% of purchase price if API fails
        if (pricingData?.purchasePrice) {
          setWhoisPrivacyPrice(pricingData.purchasePrice * 0.15);
        } else if (selectedDomain.purchasePrice) {
          setWhoisPrivacyPrice(selectedDomain.purchasePrice * 0.15);
        }
      }
    };

    fetchPrivacyPricing();
  }, [selectedDomain?.domainName, years, supportsPrivacy, pricingData]);

  // Calculate totals using API pricing data or fallback to search result pricing
  const domainPrice = pricingData?.purchasePrice ?? selectedDomain?.purchasePrice ?? 0;
  const renewalPrice = pricingData?.renewalPrice ?? selectedDomain?.renewalPrice ?? domainPrice;
  // Use the purchasePrice directly from API (it's already for the selected years)
  const domainTotal = domainPrice;
  // WHOIS privacy price from API is already for the selected years (total, not per year)
  const privacyTotal = whoisPrivacy && whoisPrivacyPrice > 0 ? whoisPrivacyPrice : 0;
  const subtotal = domainTotal + privacyTotal;
  // Assuming tax is 0% for now, can be calculated based on location if needed
  const tax = 0;
  const total = subtotal + tax;

  // Update loading state in flow
  useEffect(() => {
    updateState({
      isLoading: isLoadingPricing || isLoadingPrivacyInfo,
    });
  }, [isLoadingPricing, isLoadingPrivacyInfo, updateState]);

  // Save pricing information to state when pricing data is ready
  useEffect(() => {
    if (selectedDomain && (pricingData || selectedDomain.purchasePrice)) {
      updateState({
        domainPricing: {
          domainName: selectedDomain.domainName,
          purchasePrice: domainPrice,
          renewalPrice: renewalPrice,
          years: years,
          whoisPrivacy: whoisPrivacy,
          whoisPrivacyPrice: whoisPrivacyPrice,
          subtotal: subtotal,
          tax: tax,
          total: total,
          purchaseType: selectedDomain.purchaseType,
        },
        options: {
          ...state.options,
          whoisPrivacy: whoisPrivacy,
        },
      });
    }
  }, [pricingData, years, whoisPrivacy, whoisPrivacyPrice, domainPrice, renewalPrice, subtotal, tax, total, selectedDomain, state.options, updateState]);

  if (!selectedDomain) {
    return (
      <Card className="rounded-4 border">
        <CardBody>
          <Alert color="warning">
            No domain selected. Please go back and select a domain.
          </Alert>
          <Button color="secondary" onClick={previousStep} className="mt-3">
            Go Back
          </Button>
        </CardBody>
      </Card>
    );
  }

  const formatPrice = (price?: number): string => {
    if (price === undefined || price === null) return "$0.00";
    return `$${price.toFixed(2)}`;
  };

  const getPurchaseTypeLabel = (type?: string): string => {
    if (!type) return "Standard Registration";
    const typeMap: Record<string, string> = {
      registration: "New Registration",
      aftermarket_i: "Aftermarket (i)",
      expiring: "Expiring Domain",
      backorder: "Backorder",
      aftermarket_s: "Aftermarket (s)",
      aftermarket_b: "Aftermarket (b)",
    };
    return typeMap[type] || type;
  };

  return (
    <Row>
      <Col md={12}>
        <Card className="rounded-4 border">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="card-title mb-2">Domain Pricing & Configuration</h4>
                <p className="text-muted mb-0">
                  Review pricing and configure your domain registration
                </p>
              </div>
              {selectedDomain.premium && (
                <Badge color="warning" className="p-2" style={{ fontSize: "0.9rem" }}>
                  Premium Domain
                </Badge>
              )}
            </div>

            {/* Domain Information */}
            <Card className="mb-4" style={{ backgroundColor: "#f8f9fa" }}>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="fw-bold text-muted" style={{ fontSize: "0.85rem" }}>
                        Domain Name
                      </Label>
                      <div className="h5 mb-0" style={{ color: "#1f2937", fontWeight: 700 }}>
                        {selectedDomain.domainName}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="fw-bold text-muted" style={{ fontSize: "0.85rem" }}>
                        Purchase Type
                      </Label>
                      <div className="h6 mb-0" style={{ color: "#4b5563" }}>
                        {getPurchaseTypeLabel(selectedDomain.purchaseType)}
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Registration Period */}
            <div className="mb-4">
              <Label className="fw-bold mb-3" style={{ fontSize: "1rem" }}>
                Registration Period <span className="text-danger">*</span>
              </Label>
              <Row>
                <Col md={6}>
                  <div className="position-relative">
                    <Input
                      type="select"
                      value={years}
                      onChange={(e) => setYears(parseInt(e.target.value))}
                      style={{ fontSize: "1rem", padding: "0.75rem" }}
                      disabled={isLoadingPricing}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => (
                        <option key={year} value={year}>
                          {year} {year === 1 ? "Year" : "Years"}
                        </option>
                      ))}
                    </Input>
                    {isLoadingPricing && (
                      <div className="position-absolute" style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}>
                        <Spinner size="sm" />
                      </div>
                    )}
                  </div>
                  <small className="text-muted mt-2 d-block">
                    Select how many years you want to register this domain for
                  </small>
                  {pricingError && (
                    <Alert color="warning" className="mt-2 mb-0" style={{ fontSize: "0.85rem", padding: "0.5rem" }}>
                      {pricingError}
                    </Alert>
                  )}
                </Col>
              </Row>
            </div>

            {/* Additional Options */}
            {supportsPrivacy && (
              <div className="mb-4">
                <Label className="fw-bold mb-3" style={{ fontSize: "1rem" }}>
                  Additional Options
                </Label>
                <Card style={{ backgroundColor: "#f8f9fa" }}>
                  <CardBody>
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="whoisPrivacy"
                        checked={whoisPrivacy}
                        onChange={(e) => setWhoisPrivacy(e.target.checked)}
                        style={{ width: "3rem", height: "1.5rem", cursor: "pointer" }}
                        disabled={isLoadingPrivacyInfo || whoisPrivacyPrice === 0}
                      />
                      <Label
                        className="form-check-label ms-3"
                        htmlFor="whoisPrivacy"
                        style={{ cursor: "pointer", fontSize: "1rem" }}
                      >
                        <div className="fw-bold">WHOIS Privacy Protection</div>
                        <small className="text-muted">
                          Hide your personal information from public WHOIS databases. 
                          {isLoadingPrivacyInfo ? (
                            <span className="ms-2">
                              <Spinner size="sm" /> Loading pricing...
                            </span>
                          ) : whoisPrivacyPrice > 0 ? (
                            ` Price: ${formatPrice(whoisPrivacyPrice)} for ${years} ${years === 1 ? "year" : "years"}`
                          ) : (
                            " Pricing unavailable"
                          )}
                        </small>
                      </Label>
                    </div>
                  </CardBody>
                </Card>
              </div>
            )}

            {/* Pricing Breakdown */}
            <div className="mb-4">
              <Label className="fw-bold mb-3" style={{ fontSize: "1rem" }}>
                Pricing Breakdown
              </Label>
              <Table responsive bordered className="mb-0">
                <thead style={{ backgroundColor: "#f8f9fa" }}>
                  <tr>
                    <th style={{ fontWeight: 600 }}>Item</th>
                    <th style={{ fontWeight: 600, textAlign: "right" }}>Price</th>
                    <th style={{ fontWeight: 600, textAlign: "right" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div>
                        <div className="fw-bold">{selectedDomain.domainName}</div>
                        <small className="text-muted">
                          {getPurchaseTypeLabel(selectedDomain.purchaseType)} - {years} {years === 1 ? "year" : "years"}
                        </small>
                      </div>
                    </td>
                    <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                      {isLoadingPricing ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          {pricingData?.purchasePrice !== undefined
                            ? formatPrice(domainTotal) + ` (${years} ${years === 1 ? "year" : "years"})`
                            : formatPrice(domainPrice) + " / year"}
                        </>
                      )}
                    </td>
                    <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                      {isLoadingPricing ? (
                        <Spinner size="sm" />
                      ) : (
                        <span className="fw-bold">{formatPrice(domainTotal)}</span>
                      )}
                    </td>
                  </tr>
                  {whoisPrivacy && (
                    <tr>
                      <td>
                        <div>
                          <div className="fw-bold">WHOIS Privacy Protection</div>
                          <small className="text-muted">
                            {years} {years === 1 ? "year" : "years"}
                          </small>
                        </div>
                      </td>
                      <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                        {formatPrice(whoisPrivacyPrice)} ({years} {years === 1 ? "year" : "years"})
                      </td>
                      <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                        <span className="fw-bold">{formatPrice(privacyTotal)}</span>
                      </td>
                    </tr>
                  )}
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    <td colSpan={2} className="fw-bold" style={{ textAlign: "right" }}>
                      Subtotal:
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <span className="fw-bold">{formatPrice(subtotal)}</span>
                    </td>
                  </tr>
                  {tax > 0 && (
                    <tr>
                      <td colSpan={2} className="fw-bold" style={{ textAlign: "right" }}>
                        Tax:
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <span className="fw-bold">{formatPrice(tax)}</span>
                      </td>
                    </tr>
                  )}
                  <tr style={{ backgroundColor: "#e3f2fd", borderTop: "2px solid #2196f3" }}>
                    <td colSpan={2} className="fw-bold" style={{ textAlign: "right", fontSize: "1.1rem" }}>
                      Total:
                    </td>
                    <td style={{ textAlign: "right", fontSize: "1.1rem" }}>
                      <span className="fw-bold text-primary">{formatPrice(total)}</span>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default DomainPricing;
